// pages/CreateManualInvoice.tsx

import React, { useEffect, useRef, useState } from "react";
import { useDiscountCodesEnabled, hideDiscountBox } from "../../../hooks/useDiscountCodesEnabled";
import { Button, Card, Input, PageContainer, PageHeading, Select, Textarea } from "../../ui";
import { toast } from "react-toastify";
import {
  generateSimpleInvoice,
  getStripePaymentMethods,
  createPaymentPlan,
  chargeMemberManualPayments,
} from "../../../api/paymentsAPI";
import {
  payManualInvoiceAtDesk,
  payManualInvoiceFromBalance,
  ManualPaymentOutcome,
} from "./manualInvoicePayment";
import SelectPaymentMethodModal from "../../invoice/staff/modal/SelectPaymentMethodModal";
import { useLeadBillingPrompt, withLeadBillingChoice } from "../../../hooks/useLeadBillingPrompt";
import type { LeadBillingDecision } from "../../../hooks/useLeadBillingPrompt";
import { useDispatch, useSelector } from "react-redux";
import {
  selectMembershipDetails,
  setMembership,
} from "../../../store/slices/membershipSlice";
import {
  selectIsAdmin,
  selectPermissions,
} from "../../../store/slices/authSlice";
import MemberSearchBar from "./MemberSearchBar";
import { getMemberById } from "../../../api/membershipAPI";
import { sendNewInvoiceCreateEmail } from "../../../api/emailAPI";
import { useTheme } from "../../../context/ThemeContext";
import {
  getPrimaryText,
  getSecondaryText,
} from "../../../utils/themeUtils";
import { v4 as uuidv4 } from "uuid";
import { newAttemptKey } from "../../../utils/attemptKey";
import { getPaymentErrorMessage } from "../../../utils/paymentErrors";
import ProductAutocomplete from "../../common/dropdown/ProductAutocomplete";
import Tooltip from "../../common/Tooltip";
import PaymentPlanCreator, { PaymentPlan } from "./PaymentPlanCreator";
import {
  validateDiscountCode,
  ValidateResponse,
} from "../../../api/discountCodesAPI";
import { Tag, X } from "lucide-react";
import { getTenantId } from "../../../api/apiHelpers";
import { localTodayYmd } from "@mediatechgroup/calendar-date";
import { persistAuthToken } from "../../../api/authTokenStore";
import { defaultMethodIdFrom } from "../../../utils/savedPaymentMethods";
interface ModalProps {
  prefillMembershipId?: string;
  disableMembershipField?: boolean;
  setIsCreatedmanualInvoice?: React.Dispatch<React.SetStateAction<boolean>>;
  isCreatedManualInvoice?: boolean;
  refreshApiCalls?: number;
  setRefreshApiCalls?: React.Dispatch<React.SetStateAction<number>>;
  onClose?: () => void;
}
const CreateManualInvoice: React.FC<ModalProps> = ({
  prefillMembershipId = "",
  disableMembershipField = false,
  setIsCreatedmanualInvoice,
  isCreatedManualInvoice,
  refreshApiCalls,
  setRefreshApiCalls,
  onClose,
}) => {
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState({
    membershipId: prefillMembershipId,
    amount: "",
    note: "",
    dueDate: localTodayYmd(),
    // What this invoice is FOR. Until this selector existed every hand-keyed
    // invoice was stamped `manual_invoice`, which says nothing, so a $25
    // registration fee due 9/9 made the Monthly Dues page read September as
    // already "Generated" and hid the family from the working list (Myers @
    // GoY, 2026-08-31). The backend re-validates against the same allow-list.
    invoiceSource: "manual_invoice",
  });
  const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
  const storeClubName = localStorage.getItem("clubName");
  const [loading, setLoading] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState<string | "new">("new");
  const [paymentPlan, setPaymentPlan] = useState<PaymentPlan | null>(null);
  const [hasCardOnFile, setHasCardOnFile] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [createdInvoice, setCreatedInvoice] = useState<any>(null);
  const [savedCardLast4, setSavedCardLast4] = useState<string>("");
  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState<string | null>(null);
  const [stripeCustomerId, setStripeCustomerId] = useState<string>("");
  // HELPER
  const token = localStorage.getItem("authToken");
  const localTenantId = getTenantId() || "";
  const membership: any = useSelector(selectMembershipDetails);
  // A lead's account: one prompt before the invoice is created (never blocks).
  const { confirmLeadBilling, leadBillingPrompt } = useLeadBillingPrompt();
  const dispatch = useDispatch();
  const isAdmin = useSelector(selectIsAdmin);
  const permissions = useSelector(selectPermissions);
  const canCreatePaymentPlan = isAdmin || permissions?.some((p: any) => p.name === 'create_payment_plan');
  const [focusedUnitPriceId, setFocusedUnitPriceId] = useState<string | null>(
    null
  );
  const [focusedDiscountId, setFocusedDiscountId] = useState<string | null>(
    null
  );
  // 🔹 Line items state
  const [lineItems, setLineItems] = useState([
    {
      id: uuidv4(),
      quantity: 1,
      description: "",
      unitPrice: 0,
      discount: 0,
    },
  ]);

  // Discount code state — collapsed by default. Master toggle is enforced
  // server-side; the input simply surfaces "not valid" if the tenant has it
  // off.
  const [discountOpen, setDiscountOpen] = useState(false);
  // The club's master switch: off hides the discount code box (D9, 2026-10-10).
  const codesAccepted = useDiscountCodesEnabled();
  const [discountInput, setDiscountInput] = useState("");
  const [discountValidating, setDiscountValidating] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState<ValidateResponse["code"] | null>(null);
  const [discountAmount, setDiscountAmount] = useState<number>(0);
  const [discountError, setDiscountError] = useState<string | null>(null);

  // 🔹 Calculate total from line items
  const calculateLineTotal = (item: any) => {
    const qty = parseFloat(item.quantity) || 0;
    const price = parseFloat(item.unitPrice) || 0;
    const discount = parseFloat(item.discount) || 0;
    return qty * price - discount;
  };

  const lineItemsSubtotal = lineItems.reduce(
    (total, item) => total + calculateLineTotal(item),
    0,
  );

  // Net invoice total = line items − discount, floored at 0. The discount
  // ships down to the backend as a synthetic line item so it shows up on
  // the invoice and the recorded amount matches what's actually charged.
  const invoiceTotal = Math.max(0, lineItemsSubtotal - discountAmount);
  // 🔹 Line item handlers
  const handleLineItemChange = (id: string, field: string, value: any) => {
    setLineItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
  };

  const handleAddLineItem = () => {
    setLineItems((prev) => [
      ...prev,
      { id: uuidv4(), quantity: 1, description: "", unitPrice: 0, discount: 0 },
    ]);
  };

  const handleRemoveLineItem = (id: string) => {
    setLineItems((prev) => prev.filter((item) => item.id !== id));
  };

  // Re-validate the applied code if line-items subtotal changes — percent
  // codes scale with the new total, and an admin disabling the code mid-
  // entry should drop it.
  useEffect(() => {
    if (!appliedDiscount) return;
    let cancelled = false;
    (async () => {
      const res = await validateDiscountCode(
        appliedDiscount.code,
        "manual_invoice",
        lineItemsSubtotal,
        membership?.email || undefined,
      );
      if (cancelled) return;
      if (res.valid && res.code) {
        setDiscountAmount(Number(res.discount_amount) || 0);
      } else {
        setAppliedDiscount(null);
        setDiscountAmount(0);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItemsSubtotal, appliedDiscount?.code]);

  const handleApplyDiscount = async () => {
    const code = discountInput.trim().toUpperCase();
    if (!code) {
      setDiscountError("Enter a code.");
      return;
    }
    if (lineItemsSubtotal <= 0) {
      setDiscountError("Add line items before applying a code.");
      return;
    }
    setDiscountValidating(true);
    setDiscountError(null);
    const res = await validateDiscountCode(
      code,
      "manual_invoice",
      lineItemsSubtotal,
      membership?.email || undefined,
    );
    setDiscountValidating(false);
    if (!res.valid || !res.code) {
      setDiscountError(res.error || "That code isn't valid.");
      setAppliedDiscount(null);
      setDiscountAmount(0);
      return;
    }
    setAppliedDiscount(res.code);
    setDiscountAmount(Number(res.discount_amount) || 0);
    setDiscountError(null);
  };

  const handleRemoveDiscount = () => {
    setAppliedDiscount(null);
    setDiscountAmount(0);
    setDiscountInput("");
    setDiscountError(null);
  };

  // // Fetch membership details first
  const fetchMembershipDetails = async () => {
    try {
      const response = await getMemberById(token, membership?.id);

      // Assuming the membership details have a field `id`
      if (response && response.data) {
        dispatch(setMembership(response?.data));
      }
    } catch (err) {
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: any, payNow = false) => {
    e.preventDefault();

    // A lead's account: ask once, before anything is written. The answer is
    // kept for the rest of this attempt (the payment step of Create & Pay).
    // The page's own copy of the account is used only when it is this account;
    // otherwise the prompt looks the account up.
    const viewingThisAccount = !!membership?.id && membership.id === formData?.membershipId;
    const leadDecision = await confirmLeadBilling({
      membershipId: formData?.membershipId,
      accountType: viewingThisAccount ? membership.account_type : undefined,
      familyName: viewingThisAccount
        ? `${membership.first_name || ""} ${membership.last_name || ""}`.trim()
        : undefined,
      action: payNow ? "invoice_and_payment" : "invoice",
    });
    if (!leadDecision.proceed) return;
    leadDecisionRef.current = leadDecision;

    setLoading(true);

    try {
      // When a discount code is applied, ship it down as a synthetic
      // line item with negative unitPrice so the invoice clearly shows
      // the discount and the recorded amount matches what's charged.
      const lineItemsWithDiscount = appliedDiscount && discountAmount > 0
        ? [
            ...lineItems,
            {
              id: `discount-${appliedDiscount.id}`,
              quantity: 1,
              description: `Discount: ${appliedDiscount.code}`,
              unitPrice: -discountAmount,
              discount: 0,
            },
          ]
        : lineItems;

      const payload = {
        membershipId: formData?.membershipId,
        amount: invoiceTotal,
        paymentStrategy: "applyFunds",
        selectedCardId: selectedCardId,
        stripeCustomerId: membership?.stripe_customer_id,
        note: formData?.note,
        dueDate: formData?.dueDate, // ← Add due date to payload
        invoice_source: formData?.invoiceSource,
        lineItems: lineItemsWithDiscount,
        metadata: {
          type: "manual",
          ...(appliedDiscount && discountAmount > 0
            ? {
                discount_code: appliedDiscount.code,
                discount_amount: discountAmount,
              }
            : {}),
        },
        processed_by: {
          userId: userDetails?.id,
          name: userDetails?.first_name + " " + userDetails?.last_name,
          role: userDetails?.role,
        },
      };

      const res = await generateSimpleInvoice(token, withLeadBillingChoice(payload, leadDecision));

      if (res?.success) {
        if (res?.errorCode) {
          // ✅ handle error codes from backend
          switch (res.errorCode) {
            case "NO_CARD":
              toast.info(
                res?.message || "No card on file. Please add a payment method."
              );
              break;
            case "INSUFFICIENT_BALANCE":
              toast.info(
                res?.message || "Insufficient balance to complete this payment."
              );
              break;
            case "STRIPE_FAILED":
              toast.info(res?.message || "Stripe Failed.");
              break;
            default:
              toast.error(res?.message || "Invoice is not generated!");
          }

          // Refresh data even on error codes to ensure UI matches backend state
          if (disableMembershipField) {
            await fetchMembershipDetails();
            setIsCreatedmanualInvoice?.(!isCreatedManualInvoice);
            setRefreshApiCalls?.(prev => prev + 1); // ← Trigger refresh for Future Invoices
          }
        } else {
          // ✅ Invoice created successfully
          const invoiceId = res?.data?.invoiceId;

          // Backend redeems server-side at invoice creation time — see
          // generateSimpleInvoice → markDiscountRedeemed. No client call.

          if (!payNow) {
            // ✅ Create payment plan if enabled (only for normal create, not create & pay)
            if (paymentPlan && invoiceId) {
              try {
                const paymentPlanPayload = {
                  invoiceId,
                  membershipId: formData.membershipId,
                  totalAmount: invoiceTotal,
                  installments: paymentPlan.installments,
                  frequency: paymentPlan.frequency,
                  firstPaymentDate: paymentPlan.payFirstNow
                    ? localTodayYmd()
                    : paymentPlan.firstPaymentDate,
                  payFirstNow: paymentPlan.payFirstNow,
                  description: `Payment plan for invoice ${invoiceId}`,
                  // The plan's attempt key (PM a328e6bc). The invoice was
                  // created by THIS submit, so naming it names the attempt: a
                  // repeated request sets up one plan and charges the first
                  // installment once.
                  idempotencyKey: `plan-${invoiceId}`,
                };

                const planRes = await createPaymentPlan(
                  token,
                  withLeadBillingChoice(paymentPlanPayload, leadDecision),
                );

                if (planRes?.success) {
                  toast.success("Invoice and payment plan created successfully!");
                } else {
                  toast.warn(
                    "Invoice created, but payment plan setup failed: " +
                      (planRes?.error || planRes?.message || "Unknown error")
                  );
                }
              } catch (planError: any) {
                // The server's own words when it has some (for example: the
                // club is not set up to take card payments).
                toast.warn(
                  "Invoice created, but payment plan setup failed: " +
                    getPaymentErrorMessage(planError, planError?.message || "Unknown error")
                );
              }
            } else {
              toast.success("Invoice generated successfully!");
            }
          } else {
            toast.success("Invoice created! Select payment method.");
          }

          // ✅ Refresh data to show new invoice and updated balance
          if (disableMembershipField) {
            await fetchMembershipDetails();
            setIsCreatedmanualInvoice?.(!isCreatedManualInvoice);
            setRefreshApiCalls?.(prev => prev + 1); // ← Trigger refresh for Future Invoices
          }

          // ✅ THEN attempt email sending (non-blocking, optional)
          try {
            const emailPayload = {
              invoiceId: res?.data?.invoiceId,
              memberId: formData?.membershipId,
              clubName: storeClubName,
              message: res.message,
            };
            const emailResult = await sendNewInvoiceCreateEmail(emailPayload);

            if (emailResult && !emailResult.success && !emailResult.skipped) {
              toast.warn("Invoice created but email notification failed to send");
            }
          } catch (emailError) {
            toast.warn("Invoice created but email notification failed to send");
          }

          if (payNow) {
            // Store invoice and open payment modal
            setCreatedInvoice({
              id: invoiceId,
              amount: invoiceTotal,
              membership_id: formData.membershipId,
              tenant_id: localTenantId,
              description: formData.note,
              due_date: formData.dueDate,
            });
            setShowPaymentModal(true);
          } else {
            // Reset form and close
            setFormData({
              membershipId: prefillMembershipId,
              amount: "",
              note: "",
              dueDate: localTodayYmd(),
              invoiceSource: "manual_invoice",
            });
            setPaymentPlan(null);
            onClose?.();
          }
        }
      } else {
        // Only invoice creation errors reach here
        toast.error(res?.message || "Failed to create invoice");

        // ✅ Still refresh to ensure UI matches database state
        if (disableMembershipField) {
          await fetchMembershipDetails();
          setIsCreatedmanualInvoice?.(!isCreatedManualInvoice);
          setRefreshApiCalls?.(prev => prev + 1); // ← Trigger refresh for Future Invoices
        }
      }
    } catch (error: any) {
      toast.error("Failed to create invoice: " + error.message);

      // ✅ Always refresh after operations to ensure consistency
      if (disableMembershipField) {
        await fetchMembershipDetails();
        setIsCreatedmanualInvoice?.(!isCreatedManualInvoice);
        setRefreshApiCalls?.(prev => prev + 1); // ← Trigger refresh for Future Invoices
      }
    } finally {
      setLoading(false);
    }
  };

  // A problem with a payment stays on screen until staff close it; they
  // need to read what to do next.
  const showManualPaymentOutcome = (outcome: ManualPaymentOutcome) => {
    if (!outcome.ok) {
      toast.error(outcome.message, { autoClose: false });
      return;
    }
    toast.success(outcome.message);
    if (outcome.warning) toast.warning(outcome.warning, { autoClose: false });
  };

  // One key per payment attempt: stable across a double click.
  const attemptKeyRef = useRef<string | null>(null);
  // What staff chose on the "still a lead" prompt for this attempt.
  const leadDecisionRef = useRef<LeadBillingDecision | null>(null);

  // Handle payment after invoice creation (Create & Pay flow)
  const handleAcceptPayment = async (
    invoice: any,
    method: string,
    amount: number,
    note: any,
    receiptNumber: any,
    useSavedCard?: boolean,
    checkDetails?: any
  ) => {
    try {
      if (method === "cash" || method === "cheque") {
        // ONE request: the server writes the payment, its credit and the
        // "paid" mark (see manualInvoicePayment). The attempt key is made on
        // the first click and sent again on a second, so a double click
        // records one payment.
        if (!attemptKeyRef.current) attemptKeyRef.current = newAttemptKey();
        const outcome = await payManualInvoiceAtDesk({
          token,
          invoice,
          method,
          amount,
          note,
          receiptNumber,
          checkDetails: method === "cheque" ? checkDetails : undefined,
          idempotencyKey: attemptKeyRef.current,
          leadBilling: leadDecisionRef.current,
        });
        // Keep the key until the payment is known to be recorded: a retry
        // after "no answer" then finds the first payment instead of writing a
        // second one.
        if (outcome.ok) attemptKeyRef.current = null;
        showManualPaymentOutcome(outcome);
      }

      if (method === "card") {
        if (!stripeCustomerId || !defaultPaymentMethodId) {
          toast.info("No card on file. Please enter a payment method.");
          return;
        }
        // The amount staff typed is the amount charged (partial payments are
        // allowed; the server refuses more than the invoice). The attempt key
        // makes a double click one charge.
        if (!attemptKeyRef.current) attemptKeyRef.current = newAttemptKey();
        const paymentResponse = await chargeMemberManualPayments(token, withLeadBillingChoice({
          invoiceId: invoice?.id,
          stripeCustomerId,
          selectedCardId: defaultPaymentMethodId,
          tenantId: localTenantId,
          amount,
          note: note,
          receiptNumber: receiptNumber,
          idempotencyKey: attemptKeyRef.current,
        }, leadDecisionRef.current));
        if (paymentResponse?.success) {
          attemptKeyRef.current = null;
          toast.success(
            paymentResponse?.remainingOwed > 0
              ? paymentResponse.message
              : "Card Payment accepted successfully!"
          );
        } else {
          toast.error(
            paymentResponse?.error ||
              "Card payment failed. Please try a different payment method."
          );
        }
      }

      if (method === "account_credit") {
        // The credit is applied first and its answer is READ: this used to
        // mark the invoice paid, ignore whether any credit was applied, and
        // say "accepted successfully" either way.
        showManualPaymentOutcome(
          await payManualInvoiceFromBalance({ token, invoice, amount, note, leadBilling: leadDecisionRef.current })
        );
      }

      // Refresh data after payment
      fetchMembershipDetails();
      setRefreshApiCalls?.(prev => prev + 1);
    } catch (err: any) {
      toast.error("Error accepting payment: " + err.message);
    }
  };

  const handlePaymentSubmit = async ({
    method,
    amount,
    note,
    receiptNumber,
    useSavedCard,
    checkDetails,
  }: any) => {
    await handleAcceptPayment(
      createdInvoice,
      method,
      amount,
      note,
      receiptNumber,
      useSavedCard,
      checkDetails
    );
    // Reset form and close after payment
    setCreatedInvoice(null);
    setFormData({
      membershipId: prefillMembershipId,
      amount: "",
      note: "",
      dueDate: localTodayYmd(),
      invoiceSource: "manual_invoice",
    });
    setLineItems([
      { id: uuidv4(), quantity: 1, description: "", unitPrice: 0, discount: 0 },
    ]);
    setPaymentPlan(null);
    onClose?.();
  };

  useEffect(() => {
    if (!membership?.stripe_customer_id) return;
    const fetch = async () => {
      try {
        const response = await getStripePaymentMethods(
          token,
          membership?.stripe_customer_id
        );
        if (response?.success) {
          const cards = response?.data || [];
          const hasCards = cards.length > 0;
          setHasCardOnFile(hasCards);

          let defaultId: string | null = null;
          defaultId =
            defaultMethodIdFrom(response) || response?.data?.[0]?.id || null;

          // ✅ Auto-select default card if not already selected
          if ((!selectedCardId || selectedCardId === "new") && defaultId) {
            setSelectedCardId(defaultId);
          }
          setDefaultPaymentMethodId(defaultId);
          setSavedCardLast4(
            cards?.find?.((c: any) => c.id === defaultId)?.card?.last4 || ""
          );
          setStripeCustomerId(membership?.stripe_customer_id || "");
          // store token
          persistAuthToken(response?.token);
        }
      } catch (err) {
        toast.error("Failed to fetch saved cards");
      }
    };
    fetch();
  }, [membership?.stripe_customer_id]); // Fetch saved cards

  const invoiceForm = (
    <div className="w-full">
      {/* <h2
        className={`text-lg sm:text-xl font-bold mb-4 sm:mb-6 ${getPrimaryText(
          isDarkMode
        )}`}
      >
        Create Manual Invoice
      </h2> */}
      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
        {!disableMembershipField && (
          <div className="space-y-3">
            <label
              className={`block text-sm font-medium ${getSecondaryText(
                isDarkMode
              )}`}
            >
              Select Member
            </label>
            <MemberSearchBar
              onMemberSelect={(memberId: string) =>
                setFormData((prev) => ({ ...prev, membershipId: memberId }))
              }
            />
            {formData?.membershipId && (
              <Input
                aria-label="Membership ID"
                type="text"
                name="membershipId"
                value={formData?.membershipId}
                readOnly
                className="cursor-not-allowed"
                wrapperClassName="sm:w-1/2"
              />
            )}
          </div>
        )}

        <div className="space-y-2">
          <Textarea
            label="Notes"
            name="note"
            placeholder="Enter Notes..."
            value={formData.note}
            onChange={handleChange}
            rows={3}
            className="resize-vertical"
          />
        </div>

        {/* Due Date */}
        <div className="space-y-2">
          <Input
            label="Due Date"
            type="date" autoComplete="off"
            name="dueDate"
            value={formData.dueDate}
            onChange={handleChange}
            wrapperClassName="sm:w-1/2"
          />
        </div>

        {/* What the invoice is for — drives the Monthly Dues page, the
            reconciliation and revenue reports, and payment-method routing. */}
        <div className="space-y-2">
          <Select
            label="Invoice Type"
            name="invoiceSource"
            value={formData.invoiceSource}
            onChange={(e) =>
              setFormData({ ...formData, invoiceSource: e.target.value })
            }
            options={[
              { value: "manual_invoice", label: "Manual Invoice" },
              { value: "membership_dues", label: "Membership Dues" },
              { value: "registration_fee", label: "Registration Fee" },
            ]}
            hint={
              formData.invoiceSource === "membership_dues"
                ? "Counts as this member's dues for the due date's month — the Monthly Dues page will show them as billed."
                : formData.invoiceSource === "registration_fee"
                ? "Billed alongside dues. Never counts as dues, so the member still shows on the Monthly Dues list."
                : "General charge. Does not tell the Monthly Dues page anything either way."
            }
            wrapperClassName="sm:w-1/2"
          />
        </div>

        {/* Line Items */}
        <div data-onboarding="line-items" className="mb-6">
          <Tooltip content="Add individual items to your invoice with quantities, descriptions, unit prices, and optional discounts. Each line item will appear on the customer's invoice with calculated totals. Use this to itemize services, products, fees, or programs.">
            <h3
              className={`text-lg font-semibold mb-2 cursor-help ${getPrimaryText(
                isDarkMode
              )}`}
            >
              Invoice Line Items
            </h3>
          </Tooltip>
          {/* Column Headings */}
          <div
            className={`grid grid-cols-[4rem_1fr_7rem_6rem_7rem_1.5rem] gap-2 mb-2 font-semibold ${getSecondaryText(
              isDarkMode
            )}`}
          >
            <Tooltip content="Number of units or quantity for this line item. Multiplied by unit price to calculate line total.">
              <div className="text-center cursor-help">Qty</div>
            </Tooltip>
            <Tooltip content="Detailed description of the service, product, or fee being charged. This appears on the customer's invoice and should clearly identify what they're being billed for.">
              <div className="cursor-help">Description</div>
            </Tooltip>
            <Tooltip content="Price per unit for this item. Will be multiplied by quantity to calculate the line subtotal before any discounts are applied.">
              <div className="text-center cursor-help">Unit Price</div>
            </Tooltip>
            <Tooltip content="Optional discount amount to subtract from the line total. Enter as a fixed dollar amount, not a percentage.">
              <div className="text-center cursor-help">Discount</div>
            </Tooltip>
            <Tooltip content="Calculated line total: (Quantity × Unit Price) - Discount. This amount contributes to the invoice total.">
              <div className="text-right cursor-help">Amount</div>
            </Tooltip>
            <div></div> {/* For remove button */}
          </div>

          {lineItems?.map((item, index) => (
            <div
              key={item.id}
              className="grid grid-cols-[4rem_1fr_7rem_6rem_7rem_1.5rem] gap-2 mb-2 items-center"
            >
              <Input aria-label={`Quantity for ${item.description || "this line item"}`}
                type="number"
                placeholder="Qty"
                value={item.quantity}
                min={1}
                onChange={(e) =>
                  handleLineItemChange(item.id, "quantity", e.target.value)
                }
              />
              {/* <input
              type="text"
              placeholder="Description"
              value={item.description}
              onChange={(e) =>
                handleLineItemChange(item.id, "description", e.target.value)
              }
              className={`p-2 rounded focus:outline-none transition ${getInputStyles(
                isDarkMode
              )}`}
              onFocus={(e) => {
                e.currentTarget.style.borderColor =
                  settings?.primary_color ||
                  (isDarkMode ? "#4b5563" : "#6b7280");
                e.currentTarget.style.boxShadow = `0 0 0 2px ${
                  settings?.primary_color
                    ? settings.primary_color + "40"
                    : isDarkMode
                    ? "#4b556340"
                    : "#6b728040"
                }`;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = isDarkMode
                  ? "#4b5563"
                  : "#d1d5db";
                e.currentTarget.style.boxShadow = "none";
              }}
            /> */}
              <ProductAutocomplete
                value={item.description}
                onChange={(val, product) => {
                  handleLineItemChange(item.id, "description", val);
                  if (product) {
                    handleLineItemChange(
                      item.id,
                      "unitPrice",
                      product.price || 0
                    );
                  }
                }}
              />

              <Input aria-label={`Unit price for ${item.description || "this line item"}`}
                type="text"
                inputMode="decimal"
                placeholder="0.00"
                value={
                  focusedUnitPriceId === item.id
                    ? item.unitPrice || ""
                    : item.unitPrice !== undefined &&
                      item.unitPrice !== null &&
                      item.unitPrice !== 0
                    ? Number(item.unitPrice).toFixed(2)
                    : item.unitPrice === 0
                    ? "0.00"
                    : ""
                }
                onChange={(e) => {
                  handleLineItemChange(item.id, "unitPrice", e.target.value);
                }}
                onFocus={() => {
                  setFocusedUnitPriceId(item.id);
                }}
                onBlur={(e) => {
                  setFocusedUnitPriceId(null);
                  // Format to 2 decimal places on blur
                  const value = parseFloat(e.target.value);
                  if (!isNaN(value) && value >= 0) {
                    handleLineItemChange(
                      item.id,
                      "unitPrice",
                      value.toFixed(2)
                    );
                  } else {
                    handleLineItemChange(item.id, "unitPrice", "0.00");
                  }
                }}
              />
              <Input aria-label="Discount"
                type="text"
                inputMode="decimal"
                placeholder="0.00"
                value={
                  focusedDiscountId === item.id
                    ? item.discount || ""
                    : item.discount !== undefined &&
                      item.discount !== null &&
                      item.discount !== 0
                    ? Number(item.discount).toFixed(2)
                    : item.discount === 0
                    ? "0.00"
                    : ""
                }
                onChange={(e) => {
                  handleLineItemChange(item.id, "discount", e.target.value);
                }}
                onFocus={() => {
                  setFocusedDiscountId(item.id);
                }}
                onBlur={(e) => {
                  setFocusedDiscountId(null);
                  // Format to 2 decimal places on blur
                  const value = parseFloat(e.target.value);
                  if (!isNaN(value) && value >= 0) {
                    handleLineItemChange(item.id, "discount", value.toFixed(2));
                  } else {
                    handleLineItemChange(item.id, "discount", "0.00");
                  }
                }}
              />
              <div
                className={`text-right font-semibold ${getPrimaryText(
                  isDarkMode
                )}`}
              >
                {calculateLineTotal(item).toFixed(2)}
              </div>
              {lineItems.length > 1 ? (
                <Button
                  variant="dangerGhost"
                  size="xs"
                  iconOnly
                  type="button"
                  onClick={() => handleRemoveLineItem(item.id)}
                  aria-label="Remove line item"
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </Button>
              ) : (
                <div></div>
              )}
            </div>
          ))}

          <Tooltip content="Add another line item to this invoice. Each item can have its own quantity, description, unit price, and discount. Use multiple line items to itemize different services or products on a single invoice.">
            <Button
              type="button"
              variant="secondary"
              size="md"
              onClick={handleAddLineItem}
              className="mt-2 cursor-help"
            >
              + Add Line Item
            </Button>
          </Tooltip>
        </div>

        {/* Discount code — collapsed by default, and hidden when the club's
            master switch is off. The server checks the code again when the
            invoice is saved. */}
        {!hideDiscountBox(codesAccepted, !!appliedDiscount) && (
          <div className="mt-3 mb-3">
            {!discountOpen && !appliedDiscount ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setDiscountOpen(true)}
                icon={<Tag className="w-3.5 h-3.5" />}
              >
                Have a discount code?
              </Button>
            ) : appliedDiscount ? (
              <div className="flex items-center justify-between text-sm">
                <span className={`flex items-center gap-1 ${getSecondaryText(isDarkMode)}`}>
                  <Tag className="w-3.5 h-3.5" />
                  Code <span className="font-mono">{appliedDiscount.code}</span> applied — saved $
                  {discountAmount.toFixed(2)}
                </span>
                <Button
                  variant="dangerGhost"
                  size="sm"
                  type="button"
                  onClick={handleRemoveDiscount}
                >
                  Remove
                </Button>
              </div>
            ) : (
              <div>
                <div className="flex gap-2 max-w-md">
                  <Input
                    aria-label="Voucher code"
                    type="text"
                    placeholder="Enter code"
                    value={discountInput}
                    onChange={(e) => setDiscountInput(e.target.value.toUpperCase())}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleApplyDiscount();
                      }
                    }}
                    className="font-mono uppercase"
                    fullWidth={false}
                    wrapperClassName="flex-1"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    size="md"
                    onClick={handleApplyDiscount}
                    disabled={discountValidating || !discountInput.trim()}
                  >
                    {discountValidating ? "…" : "Apply"}
                  </Button>
                </div>
                {discountError && (
                  <p className={`text-xs mt-1 ${isDarkMode ? "text-red-400" : "text-red-600"}`}>
                    {discountError}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        <div
          className={`text-right text-xl font-bold mb-6 ${getPrimaryText(
            isDarkMode
          )}`}
        >
          {appliedDiscount && discountAmount > 0 && (
            <div className={`text-sm font-normal ${getSecondaryText(isDarkMode)}`}>
              Subtotal: USD {lineItemsSubtotal.toFixed(2)} − Discount: USD {discountAmount.toFixed(2)}
            </div>
          )}
          Total: USD {invoiceTotal.toFixed(2)}
        </div>

        {/* Payment Plan Creator - Only visible to admins or users with create_payment_plan permission */}
        {formData.membershipId && canCreatePaymentPlan && (
          <PaymentPlanCreator
            invoiceAmount={invoiceTotal}
            onPlanChange={setPaymentPlan}
            membershipId={formData.membershipId}
            hasCardOnFile={hasCardOnFile}
          />
        )}

        <div className="pt-4 flex flex-col sm:flex-row gap-3">
          <Button
            type="submit"
            variant="primary"
            size="md"
            disabled={loading}
            className="w-full sm:w-auto"
          >
            {loading ? "Creating..." : "Create Invoice"}
          </Button>
          <button
            type="button"
            disabled={loading}
            onClick={(e) => handleSubmit(e, true)}
            className="w-full sm:w-auto inline-flex items-center justify-center text-white px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: loading ? "#9CA3AF" : "#16a34a" }}
          >
            {loading ? "Creating..." : "Create & Pay"}
          </button>
        </div>
      </form>

      {leadBillingPrompt}

      <SelectPaymentMethodModal
        isOpen={showPaymentModal}
        onClose={() => {
          setShowPaymentModal(false);
          setCreatedInvoice(null);
          // Reset form since invoice was already created
          setFormData({
            membershipId: prefillMembershipId,
            amount: "",
            note: "",
            dueDate: localTodayYmd(),
            invoiceSource: "manual_invoice",
          });
          setLineItems([
            { id: uuidv4(), quantity: 1, description: "", unitPrice: 0, discount: 0 },
          ]);
          setPaymentPlan(null);
          onClose?.();
        }}
        onSubmit={handlePaymentSubmit}
        invoice={createdInvoice}
        leadBilling={leadDecisionRef.current}
        savedCardLast4={savedCardLast4}
        fetch={() => {
          fetchMembershipDetails();
          setRefreshApiCalls?.(prev => prev + 1);
        }}
      />
    </div>
  );

  // Inside a modal (Billing History passes onClose) the form is the whole
  // surface. As the /manage/billing/invoice/new route it is a page, so it
  // takes the standard page frame and heading.
  if (onClose) return invoiceForm;

  return (
    <PageContainer width="form">
      <PageHeading title="New Invoice" />
      <Card>{invoiceForm}</Card>
    </PageContainer>
  );
};
export default CreateManualInvoice;
